import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { api, apiError, eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Users, Copy, Check, Pencil } from "lucide-react";
import { t } from "@/lib/i18n";

export default function Agents() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", commission: 0 });

  const { data: agents = [], isLoading } = useQuery({ queryKey: ["agents"], queryFn: async () => (await api.get("/agents")).data });

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/agents", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); toast.success(t("Agente aggiunto")); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const update = useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.put(`/agents/${id}`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); toast.success(t("Agente aggiornato")); setOpen(false); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/agents/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); toast.success(t("Agente rimosso")); },
  });

  const openNew = () => { setEditing(null); setForm({ name: "", email: "", password: "", commission: 0 }); setOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ name: a.name, email: a.email, password: "", commission: a.commission || 0 }); setOpen(true); };

  const submit = (e) => {
    e.preventDefault();
    if (editing) update.mutate({ id: editing.agent_id, name: form.name, commission: Number(form.commission) });
    else create.mutate({ name: form.name, email: form.email, password: form.password, commission: Number(form.commission) });
  };

  const copyCode = () => { navigator.clipboard.writeText(user?.invite_code || ""); setCopied(true); toast.success(t("Codice copiato")); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Rete agenti")}</h1>
          <p className="text-muted-foreground text-sm mt-1">{agents.length} {t("agenti collegati")}</p>
        </div>
        <Button onClick={openNew} className="gap-2 font-semibold" data-testid="add-agent-btn"><Plus className="h-4 w-4" /> {t("Nuovo agente")}</Button>
      </div>

      <div className="bg-white border border-border rounded-xl p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("Codice invito azienda")}</p>
          <p className="font-display text-2xl font-extrabold font-mono-j tracking-wide mt-1" data-testid="invite-code">{user?.invite_code}</p>
          <p className="text-xs text-muted-foreground mt-1">{t("Gli agenti possono registrarsi gratis con questo codice.")}</p>
        </div>
        <Button variant="outline" onClick={copyCode} className="gap-2" data-testid="copy-invite-btn">
          {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}{copied ? t("Copiato") : t("Copia")}
        </Button>
      </div>

      {isLoading ? <p className="text-muted-foreground">{t("Caricamento…")}</p> : agents.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center">
          <Users className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">{t("Nessun agente. Creane uno o condividi il codice invito.")}</p>
        </div>
      ) : (
        <div className="bg-white border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-muted-foreground text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">{t("Agente")}</th>
                  <th className="text-left px-5 py-3 font-medium">{t("Email")}</th>
                  <th className="text-right px-5 py-3 font-medium">{t("Provvigione")}</th>
                  <th className="text-right px-5 py-3 font-medium">{t("Ordini")}</th>
                  <th className="text-right px-5 py-3 font-medium">{t("Venduto")}</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {agents.map((a) => (
                  <tr key={a.agent_id} className="border-t border-border hover:bg-secondary/40" data-testid={`agent-row-${a.agent_id}`}>
                    <td className="px-5 py-3 font-medium">{a.name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{a.email}</td>
                    <td className="px-5 py-3 text-right">{a.commission || 0}%</td>
                    <td className="px-5 py-3 text-right">{a.orders_count}</td>
                    <td className="px-5 py-3 text-right font-semibold text-primary">{eur(a.total_sales)}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(a)} data-testid={`edit-agent-${a.agent_id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="text-destructive" onClick={() => remove.mutate(a.agent_id)} data-testid={`delete-agent-${a.agent_id}`}><Trash2 className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t("Modifica agente") : t("Nuovo agente")}</DialogTitle>
            <DialogDescription>{editing ? t("Aggiorna nome e provvigione dell'agente.") : t("Crea le credenziali o invita un agente esistente.")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2"><Label>{t("Nome *")}</Label><Input data-testid="agent-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>{t("Email *")}</Label><Input data-testid="agent-email-input" type="email" required disabled={!!editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              {!editing && <div className="space-y-2"><Label>{t("Password")}</Label><Input data-testid="agent-password-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={t("per nuovo agente")} /></div>}
              <div className="space-y-2"><Label>{t("Provvigione %")}</Label><Input data-testid="agent-commission-input" type="number" step="0.1" min="0" value={form.commission} onChange={(e) => setForm({ ...form, commission: e.target.value })} /></div>
            </div>
            {!editing && <p className="text-xs text-muted-foreground">{t("Se l'email è già di un agente esistente, verrà solo collegato alla tua azienda (lascia la password vuota).")}</p>}
            <DialogFooter><Button type="submit" disabled={create.isPending || update.isPending} className="font-semibold" data-testid="save-agent-btn">{(create.isPending || update.isPending) ? t("Salvataggio…") : t("Salva")}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
